interface UrlMakerParameter {
    Name: string;
    Value: string;
}

type UrlMakerToken = string | number | UrlMakerParameter;

/**
 * The UrlMaker class provides a way to compose an url in a robust way, using a modern approach
 * in a "builder pattern" style.
 * Its intent is to avoid 'manual' url composition that leads to errors.
 *
 * @public
 */

export class UrlMaker {
    constructor(private baseUrl: string | undefined = '', private tokens: Array<UrlMakerToken> | undefined = new Array<string>()) {
        return this;
    }

    /**
     * Returns the complete url
     * @returns Returns a url
     */

    toString(): string {
        if (!this.baseUrl || !this.tokens) {
            return '';
        }

        if (this.baseUrl[this.baseUrl.length - 1] !== '/') {
            this.baseUrl += '/';
        }

        return this.baseUrl + this.generateUrl(this.tokens);
    }

    /**
     * Set Base Url
     *
     * @param baseUrl the base url (ex: contoso.com)
     * @param value its value
     * @returns 'this'
     */

    appendBaseUrl(baseUrl: string) {
        this.baseUrl = baseUrl;
        return this;
    }

    /**
     * Appends a segment to the composed url
     *
     * @param segment the segment that will be appended to the url
     * @returns 'this'
     */
    appendSegment(segment: string | number) {
        if (this.tokens === undefined) {
            return;
        }

        if (typeof segment === 'string') {
            segment = this.parse(segment);
        }

        const token: UrlMakerToken = segment;
        this.tokens.push(token);
        return this;
    }

    /**
     * Appends a parameter to the token list
     *
     * @param param the name of the param
     * @param value its value
     * @returns 'this'
     */

    appendParameter(param: string, value: string | number | boolean) {
        if (this.tokens === undefined) {
            return;
        }

        let parsedValue: string = typeof value === 'number' || typeof value === 'boolean' ? value.toString() : value;

        const urlParam: UrlMakerParameter = {
            Name: param,
            Value: parsedValue,
        };

        this.tokens.push(urlParam);
        return this;
    }

    private stripProtocols(baseUrl) {
        let protocols = ['http://', 'https://', 'ftp://', 'file://', 'mailto:'];
        var expStr = protocols.join('|');
        return baseUrl.replace(new RegExp(expStr, 'gi'), '');
    }

    private parse(segment: string) {
        return this.sanitize(segment);
    }

    private sanitize(rawUrl: string) {
        return rawUrl.replace(/\//g, '');
    }

    private generateUrl(tokens: Array<UrlMakerToken>): string {
        if (tokens === undefined) {
            return '';
        }

        const segments: Array<string> = tokens.reduce((filtered, item) => {
            if (typeof item === 'number' || typeof item === 'string') {
                if (typeof item === 'number') {
                    filtered.push(item.toString());
                } else {
                    filtered.push(item);
                }
            }
            return filtered;
        }, []);

        const composedUrl = segments.join('/');

        const allParameters: Array<UrlMakerParameter> = tokens.reduce((filtered, item) => {
            if (typeof item !== 'number' && typeof item !== 'string') {
                filtered.push(item as UrlMakerParameter);
            }
            return filtered;
        }, []);

        let parsedParametersMask = '?';
        let parsedParameterList: Array<string> = new Array<string>();

        allParameters.forEach((i) => {
            parsedParameterList.push(`${i.Name}=${i.Value}`);
        });

        // allParameters.length > 0 ? console.log(composedUrl + parsedParametersMask + parsedParameterList.join('&')) : console.log(composedUrl);

        let finalURI = allParameters.length > 0 ? composedUrl + parsedParametersMask + parsedParameterList.join('&') : composedUrl;
        return encodeURI(finalURI);
    }
}
